import type { AccountInfo, AuthenticationResult, SilentRequest } from "@azure/msal-browser"

/**
 * Unified interface for getting authentication tokens regardless of auth method
 */
export type TokenProvider = {
  /**
   * Get the authorization header value for API calls
   * Returns null if no token is available (for cookie-based auth)
   */
  getAuthorizationHeader(): Promise<string | null>

  /**
   * Check if the current auth method requires explicit token handling
   */
  requiresToken(): boolean
}

/**
 * Cookie-based authentication - no token needed, browser handles cookies automatically
 */
export function createCookieTokenProvider(): TokenProvider {
  return {
    getAuthorizationHeader: async () => null,
    requiresToken: () => false
  }
}

/**
 * MSAL-based authentication - provides Bearer tokens.
 * Throws errors to caller for handling (including InteractionRequiredAuthError).
 */
export function createMsalTokenProvider(
  tokenProvider: {
    acquireTokenSilent: (request: SilentRequest) => Promise<AuthenticationResult>
  },
  account: AccountInfo | undefined,
  scopes: string[]
): TokenProvider {
  const getAuthorizationHeader = async (): Promise<string | null> => {
    if (!account) {
      throw new Error("No account available for token acquisition")
    }

    const tokenRequest = { scopes, account }
    const response = await tokenProvider.acquireTokenSilent(tokenRequest)
    return `Bearer ${response.accessToken}`
  }

  return {
    getAuthorizationHeader,
    requiresToken: () => true
  }
}
